import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Flag, 
  Briefcase, 
  Activity,
  CheckCircle,
  Clock,
  RefreshCw,
  Bell,
  Shield,
  AlertCircle,
  Eye,
  ChevronRight
} from 'lucide-react';
import adminModerationService from '../../services/adminModerationService';

const AdminDashboard = ({ onQuickAction, refreshTrigger }) => {
  const [stats, setStats] = useState({
    pendingReports: 0,
    suspendedUsers: 0,
    flaggedJobs: 0,
    todayActions: 0
  });
  const [recentReports, setRecentReports] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, [refreshTrigger]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [statsResult, reportsResult, activityResult] = await Promise.all([
        adminModerationService.getModerationStats(),
        adminModerationService.getReports(),
        adminModerationService.getRecentActions(5)
      ]);
      
      if (statsResult.success) {
        setStats(statsResult.data);
      } else {
        setError(statsResult.error || 'Failed to load dashboard data');
      }

      if (reportsResult.success) {
        // Show only the 5 most recent submitted/pending reports
        const pending = reportsResult.data
          .filter(r => r.status === 'submitted' || r.status === 'pending')
          .slice(0, 5);
        setRecentReports(pending);
      }

      if (activityResult.success) {
        setRecentActivity(activityResult.data);
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('An unexpected error occurred while loading dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    if (onQuickAction) {
      onQuickAction(action);
    }
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const formatActionType = (type) => {
    return (type || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const actionColor = (type) => {
    if (!type) return 'text-gray-500';
    if (type.includes('suspend') || type.includes('delete')) return 'text-red-500';
    if (type.includes('warn')) return 'text-yellow-500';
    if (type.includes('resolve') || type.includes('approve') || type.includes('unsuspend')) return 'text-green-500';
    return 'text-blue-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-red-800">Error Loading Dashboard</h3>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
          <button
            onClick={handleRefresh}
            className="bg-red-100 hover:bg-red-200 text-red-800 px-4 py-2 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with refresh button */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Admin Dashboard</h2>
        <div className="flex items-center space-x-4">
          {lastUpdated && (
            <span className="text-sm text-gray-500">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={handleRefresh}
            className="flex items-center space-x-2 px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Reports</p>
              <p className="text-2xl font-bold text-red-600">{stats.pendingReports}</p>
              <p className="text-xs text-gray-500 mt-1">Requires review</p>
            </div>
            <Flag className="w-8 h-8 text-red-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Suspended Users</p>
              <p className="text-2xl font-bold text-orange-600">{stats.suspendedUsers}</p>
              <p className="text-xs text-gray-500 mt-1">Currently suspended</p>
            </div>
            <Users className="w-8 h-8 text-orange-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Flagged Jobs</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.flaggedJobs}</p>
              <p className="text-xs text-gray-500 mt-1">Needs attention</p>
            </div>
            <Briefcase className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Today's Actions</p>
              <p className="text-2xl font-bold text-blue-600">{stats.todayActions}</p>
              <p className="text-xs text-gray-500 mt-1">Moderation actions</p>
            </div>
            <Activity className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Recent Reports & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Reports */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center">
              <Bell className="w-5 h-5 mr-2 text-red-500" />
              Recent Reports
            </h3>
            <button
              onClick={() => handleQuickAction('reports')}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
            >
              View all <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
          {recentReports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
              <CheckCircle className="w-8 h-8 mb-2 text-green-400" />
              <p className="text-sm">No pending reports</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {recentReports.map((report) => (
                <li
                  key={report.id}
                  className="py-3 flex items-start justify-between hover:bg-gray-50 rounded px-1 cursor-pointer transition-colors"
                  onClick={() => handleQuickAction('reports')}
                >
                  <div className="flex items-start space-x-3 min-w-0">
                    <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {report.reporter?.full_name || 'Unknown'} reported{' '}
                        {report.reported_user?.full_name || report.reported_job?.title || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-500 truncate capitalize">
                        {report.reason || report.report_type || 'No reason given'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end ml-2 shrink-0">
                    <span className="text-xs text-gray-400">{timeAgo(report.created_at)}</span>
                    <span className="mt-1 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">
                      {report.status}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center">
              <Shield className="w-5 h-5 mr-2 text-blue-500" />
              Recent Activity
            </h3>
            <span className="text-xs text-gray-400 flex items-center">
              <Clock className="w-3 h-3 mr-1" /> Last 5 actions
            </span>
          </div>
          {recentActivity.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
              <Activity className="w-8 h-8 mb-2" />
              <p className="text-sm">No recent activity</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {recentActivity.map((action) => (
                <li key={action.id} className="py-3 flex items-start justify-between">
                  <div className="flex items-start space-x-3 min-w-0">
                    <Eye className={`w-4 h-4 mt-0.5 shrink-0 ${actionColor(action.action_type)}`} />
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${actionColor(action.action_type)}`}>
                        {formatActionType(action.action_type)}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        by {action.admin?.full_name || 'Admin'}
                        {action.reason ? ` — ${action.reason}` : ''}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 ml-2 shrink-0">
                    {timeAgo(action.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

          <div className="flex flex-col md:flex-row md:space-x-6 space-y-6 md:space-y-0">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Activity className="w-5 h-5 mr-2" />
          Quick Actions
        </h3>
        <div className="space-y-3">
          <button 
            onClick={() => handleQuickAction('reports')}
            className="w-full text-left p-3 rounded-lg hover:bg-gray-50 flex items-center justify-between transition-colors"
          >
            <div className="flex items-center">
              <Flag className="w-4 h-4 text-red-500 mr-3" />
              <span>Review Submitted Reports</span>
            </div>
            {stats.pendingReports > 0 && (
              <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full">
                {stats.pendingReports}
              </span>
            )}
          </button>
          
          <button 
            onClick={() => handleQuickAction('users')}
            className="w-full text-left p-3 rounded-lg hover:bg-gray-50 flex items-center justify-between transition-colors"
          >
            <div className="flex items-center">
              <Users className="w-4 h-4 text-orange-500 mr-3" />
              <span>Manage Suspended Users</span>
            </div>
            {stats.suspendedUsers > 0 && (
              <span className="bg-orange-100 text-orange-600 text-xs px-2 py-1 rounded-full">
                {stats.suspendedUsers}
              </span>
            )}
          </button>
          
          <button 
            onClick={() => handleQuickAction('jobs')}
            className="w-full text-left p-3 rounded-lg hover:bg-gray-50 flex items-center justify-between transition-colors"
          >
            <div className="flex items-center">
              <Briefcase className="w-4 h-4 text-yellow-500 mr-3" />
              <span>Review Flagged Jobs</span>
            </div>
            {stats.flaggedJobs > 0 && (
              <span className="bg-yellow-100 text-yellow-600 text-xs px-2 py-1 rounded-full">
                {stats.flaggedJobs}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
          System Status
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm">Database: Online</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm">Authentication: Active</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm">Moderation: Enabled</span>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default AdminDashboard;
